/**
 * Responsibility: Implement prompts behavior for the ai layer.
 * Scope: Owns ai-layer concerns only; cross-layer orchestration is excluded.
 */
import type { GameState, LogicUpdate, CombatState, Ability, NPC, ActionEnvelope } from "@anyquest/shared-core"
import { CombatStatus } from "@anyquest/shared-core"
import type { PreforgeRecipe, PreforgeDraft, PreforgeSourceWorldRef } from '../commerce/commerce-contract'
import { formatDay, formatClock, totalMinFromClock } from '../engine/temporal'
import { formatVitalValue } from '../engine/vitals'
import { formatProgressClock, locationKey, HUMAN_AVG_WALKING_SKILL_LEVEL, SKILL_LEVEL_DOUBLING_DELTA } from '../engine/state'
import { isCompanionPresent } from '../engine/companions'
// ============================================================
// PROMPTS.TS — System prompt string constants
// AI-facing prompts are compact and information-dense.
// Player-facing text (prose, narration) is authored by AI in
// a separate voice and never mixes with system context.
// ============================================================

export const ACTION_VALIDATOR_SYSTEM = `You are the Action Validator. Your job is to protect the game's immersion and consistency.
Receive a player action and the game context. Respond with a single JSON object:
- If the action is plausible, consistent with the character, and physically possible in the current scene: {"verdict": "ok"}
- If the action is nonsensical, out-of-character, fourth-wall-breaking, or physically impossible: {"verdict": "bad"}
- If the action attempts to force a specific outcome or dictate NPC behavior against their will: {"verdict": "bad"}

IMPORTANT: When in doubt, return {"verdict": "ok"}. Only block actions that are clearly impossible or break immersion.
Mere difficulty, danger, or low probability is NOT a reason to block. Players can attempt foolish things.

Examples of "bad" actions:
- "I check my email." (out of genre)
- "I teleport to the king's castle." (impossible without specific magic)
- "I remember that I am a player in a video game." (fourth-wall break)
- "The goblin spontaneously explodes." (controlling the world, not attempting an action)

Examples of "ok" actions even if risky:
- "I try to convince the dragon to give me its gold." (attempting social, outcome decided by engine)
- "I jump across the chasm." (dangerous but physically possible)
- "I attack the guard." (legal action even if unwise)

Your only output must be the JSON object. No other text.`
const STAT_KEYS = 'STR,DEX,CON,INT,WIS,CHA,PER,LCK,WIL,FOR,AGI,PRS'

export const VESSEL_GENESIS_SYSTEM = `You are the Vessel Weaver. Define the player's character (the "Vessel") based on their bio and the world's reality.
Output strict JSON:
{
  "name": string,
  "identity": string,
  "sex": string,
  "stats": { "STR":n,"DEX":n,"CON":n,"INT":n,"WIS":n,"CHA":n,"PER":n,"LCK":n,"WIL":n,"FOR":n,"AGI":n,"PRS":n },
  "traits": [ { "name": string, "desc": string } ],
  "skills": [ { "id": string, "name": string, "level": number, "desc": string } ],
  "abilities": [ { "id": string, "name": string, "desc": string, "cooldown": number } ],
  "inventory": string[],
  "quests": [
    { "id": string, "title": string, "desc": string, "type": "quest"|"mini-quest"|"want"|"wish", "status": "active"|"potential" }
  ]
}
Rules:
- name: 1-3 words. Avoid generic AI names. Fits world culture.
- identity: rewrite submitted concept into a polished archetype phrase.
- stats: 8-16 range, calibrated to bio.
- traits: Exactly 3 reflecting bio.
- skills: 4-8 practical proficiencies. Scale rule: human walking is level ${HUMAN_AVG_WALKING_SKILL_LEVEL}.
- inventory: 2-5 starter items.
- quests: 1-3 starting goals.`

export const SCENARIO_GENESIS_SYSTEM = `You are the Scenario Initiate. Define the starting location and immediate situation.
Output strict JSON:
{
  "room": {
    "name": string,
    "desc": string,
    "narration": string,
    "items": string[],
    "npcs": [
      { "name": string, "identity": string, "lore": string, "sex": string,
        "intentions": string[],
        "feelings": { "<target>": { "sentiment": "positive"|"negative"|"neutral", "intensity": n } },
        "relationship": number,
        "memory": string[],
        "inventory": string[]
      }
    ],
    "companions": [
      {
        "name": string, "identity": string, "bio": string, "sex": string,
        "vitals": { "vitality": 100, "spirit": 100, "essence": 100 },
        "attributes": { "STR":n,"DEX":n,"CON":n,"INT":n,"WIS":n,"CHA":n,"PER":n,"LCK":n,"WIL":n,"FOR":n,"AGI":n,"PRS":n },
        "skills": [ { "id": string, "name": string, "level": number, "desc": string } ],
        "abilities": [ { "id": string, "name": string, "desc": string, "cooldown": number } ],
        "traits": [ { "name": string, "desc": string } ],
        "inventory": string[],
        "motives": [ { "desc": string, "priority": "high"|"medium"|"low" } ],
        "feelings": [ { "target": "Player", "sentiment": "positive"|"negative"|"neutral", "intensity": n } ]
      }
    ],
    "suggestions": [ { "short": string, "full": string } ]
  }
}
Rules:
- Generate an immersive starting room and situation.
- desc: technical description (AI context only).
- narration: 2-3 sentences of immersive prose (shown to player).
- optionally 1-2 companions and 1-2 NPCs.`

export const GENESIS_SYSTEM = `You are the Reality Forge. Initialize a new game world.
Rules:
- Never create NPC names that are meta/system roles (e.g. Narrator, Supervisor, Game Master, Logic Forge, Reality Forge).
... (existing GENESIS_SYSTEM logic remains for legacy compatibility, but we will use the staged calls) ...`
export const LOGIC_FORGE_SYSTEM = `You are the Logic Forge — a pure systems architect. Write deterministic math, not narrative.
Output strict JSON:
{
  "logicFn": "(state, roll, ctx) => { return { successLevel: number, updates: { vitality?: number, spirit?: number, essence?: number, timeCost: number, mods?: { [statKey]: { val: number, label: string, source: string } }, inventoryAdd?: Array<string|{ name: string, worth?: number, desc?: string, icon?: string, actions?: [{ id: string, label: string, inputTemplate: string, mode: 'test'|'do', category: 'examine'|'take'|'use'|'attack'|'move'|'interact'|'ability'|'other', when?: 'inventory'|'room'|'any', targetHint?: 'none'|'self'|'active_npc', execution?: 'shared'|'item_forge' }] }>, inventoryRemove?: string[], roomItemsAdd?: Array<string|{ name: string, worth?: number, desc?: string, icon?: string, actions?: [{ id: string, label: string, inputTemplate: string, mode: 'test'|'do', category: 'examine'|'take'|'use'|'attack'|'move'|'interact'|'ability'|'other', when?: 'inventory'|'room'|'any', targetHint?: 'none'|'self'|'active_npc', execution?: 'shared'|'item_forge' }] }>, roomItemsRemove?: string[], roomFlags?: { [flagKey]: string|number|boolean|null }, clockUpdates?: { [clockKey]: number }, npcRelationshipUpdates?: { [npcName]: number }, npcMemoryAdd?: { [npcName]: string }, scheduleJobs?: [{ kind: 'trigger_event'|'world_log', delayMinutes: number, note?: string, event?: { id: string, title: string, desc: string, imageKind?: 'item'|'npc'|'scene'|'companion', imageSeed?: string, choices: [{ label: string, input: string }] } }], artifactReplacements?: { activeEvent?: { id: string, title: string, desc: string, imageKind?: 'item'|'npc'|'scene'|'companion', imageSeed?: string, choices: [{ label: string, input: string }] }|null } } }; }",
  "meta": { "cacheable": boolean, "category": "examine|take|use|attack|move|interact|other" },
  "mechanics": { "dc": number, "primaryStat": "STR"|"DEX"|"CON"|"INT"|"WIS"|"CHA"|"PER"|"LCK"|"WIL"|"FOR"|"AGI"|"PRS", "bonus": number, "chance": number }
}
Rules:
- successLevel: 0=critical fail, 1=partial, 2=full success.
- mechanics: For ANY action where failure is possible and interesting (attacking, struggling, leaping, persuading, picking a lock, bypassing security, etc), you MUST provide a Difficulty Class (DC), the primary Attribute used, the total bonus from that attribute and modifiers, and the % chance of successLevel >= 1. Do NOT provide mechanics for trivial cacheable actions like "examine".
- mechanics consistency is mandatory: if (roll + mechanics.bonus) >= mechanics.dc then successLevel MUST be 1 or 2. If below DC then successLevel MUST be 0.
- Stats: ${STAT_KEYS}. Read as state.player.attributes[key].val (this state.player is the acting entity snapshot).
- Skills: read as state.player.skills[].
- Skill scale is logarithmic: level ${HUMAN_AVG_WALKING_SKILL_LEVEL} is average human walking, +${SKILL_LEVEL_DOUBLING_DELTA} levels is ~2x proficiency.
- For relevant actions, derive skill impact from relativeSkill = 2^((level-${HUMAN_AVG_WALKING_SKILL_LEVEL})/${SKILL_LEVEL_DOUBLING_DELTA}) and reflect it in mechanics.bonus/chance.
- timeCost: always present. Min 5 min, typical 15–30. Combat: 5 min per round.
- VITALS ARE SCALARS WITH SEPARATE MAXIMA. Read them as current/max values (for example VIT 72/94). All vitals deltas are scalar-point changes from the actor's current pool (negative = cost/damage, positive = gain). Scale damage proportionally: a weak attack = -3 to -8, moderate = -8 to -15, heavy = -15 to -25. Never exceed -40 in a single action. CON modifies damage resistance: high CON should reduce vitality loss.
- For ATTACK actions: vitalityDelta = 0 (player attacks, NPC counterattacks in a separate step). Only set spirit/essence costs if the attack uses a magical resource.
- Respect actor context from prompt ("Actor: ..."). If actor is a companion, evaluate intent from that actor's role, but return updates in the shared LogicUpdate contract only.
- ctx is the execution context. Read ctx.action.actor / ctx.action.targets / ctx.worldTime when you need explicit actor-target or timing context instead of inferring from prose alone.
- Pronoun mapping is strict: in the action text, first-person pronouns ("I", "me", "my", "myself") always refer to the acting entity from Actor context. For player actions this is the player character, never an NPC.
- Use the canonical player name from prompt context. Never invent or rename the player (no aliases/new personal names).
- Keep entity references grounded to names present in prompt context (player, companions, room NPCs, known named objects).
- Companions marked [AWAY] are not in the current location and cannot participate in actions or be targeted.
- Never materialize meta/system pseudo-entities as NPCs. Forbidden names include Narrator, Supervisor, Game Master, Logic Forge, Reality Forge, and similar system-role labels.
- For SOCIAL/PERSUASION (convince, seduce, deceive, negotiate, charm): use CHA+PRS as primary stats, WIL for resistance. successLevel based on (CHA.val + PRS.val)/2 + roll vs threshold 18. Spirit cost -3 to -8 for tense social exchanges. Update npcRelationshipUpdates (range -100 to 100) and npcMemoryAdd (compact string) to reflect the outcome. Use state.currentRoom.npcs[].relationship for context.
- For MOVEMENT/TRAVEL: use AGI+DEX. timeCost 15–60 min. No vitals delta for casual movement.
- For EXAMINE/SEARCH: always successLevel=2 (cacheable). timeCost 5 min. No vitals delta.
- For INTERACTION/USE (pick lock, craft, use item, sit): use relevant stat (DEX for finesse, INT for puzzle, STR for force). Result varies with roll.
- roomFlags: Use this to track environmental state (e.g. {"door_locked": false}, {"chair_occupied": true}). Set to null to remove a flag.
- clockUpdates: use this to advance or relieve active progress clocks. Prefer scoped keys already present in prompt context such as "room:guard_alarm" or "world:famine". Positive numbers advance, negative numbers reduce.
- scheduleJobs: use this for delayed consequences that should happen after in-game time passes. Allowed jobs are:
  * { kind: "world_log", delayMinutes, note }
  * { kind: "trigger_event", delayMinutes, note?, event }
  Keep jobs rare, typed, and compact. Never schedule raw JS or vague future work.
- For HEAL/REST/SLEEP actions: use CON+WIS. timeCost 30–120 min. vitalityDelta +5 to +30 (scaled by roll and CON.val). spiritDelta +3 to +15. Full rest (sleep) gives higher deltas. successLevel=2 always for rest, scaled by roll for partial healing.
- For TAKE/PICK UP/GRAB item actions: successLevel=2 (cacheable). inventoryAdd with objects { name: string, worth: number }. roomItemsRemove with the item name. timeCost=5.
- When an item should expose explicit repeated verbs beyond generic improvise-use, you may attach "actions" to inventoryAdd / roomItemsAdd items using compact descriptors:
  * { id, label, inputTemplate, mode: "test"|"do", category, when?: "inventory"|"room"|"any", targetHint?: "none"|"self"|"active_npc", execution?: "shared"|"item_forge" }
  * For now, prefer execution="shared". Use "item_forge" only for future explicit item-owned behavior, not for normal repeated verbs.
  * Use this sparingly for obvious items like letters, healing draughts, bombs, torches, proofs, seals, relics.
- ITEM PRESENTATION (Proof Steps): If the player SHOWS, PRESENTS, or MENTIONS a specific item from their inventory to an NPC to prove a point, validate a claim, or trigger a reaction:
  * Reward this with successLevel=2 if logical.
  * npcRelationshipUpdates: +5 to +15.
  * Optionally trigger a questUpdate or roomFlag change if the item is a "key" to a puzzle or narrative lock.
- ITEM WORTH ESTIMATION: When adding items to inventory (inventoryAdd), estimate their worth in the world's currency.
  * Basis: 1 day of basic food (rationCost from prompt) is the baseline (Value 1.0).
  * Common items (junk, tools, basic supplies): 0.5x to 5x rationCost.
  * Rare/Complex items (weapons, specialized tech, jewelry): 10x to 100x rationCost.
  * Unique/Legendary items: 500x+ rationCost.
  * Factor in SCARCITY: Items that are rare in this specific world seed are worth significantly more.
- For DROP/PUT DOWN item actions: successLevel=2 (cacheable). inventoryRemove with the item name. roomItemsAdd with { name: string, worth: number }. timeCost=5.
- For TRADE actions ("Trade: I give X to NPC and receive Y"): remove the given items from inventoryRemove[], add the received items via inventoryAdd[]. Match item names exactly as they appear in inventory. successLevel=2, timeCost=5. No vitals delta. npcRelationshipUpdates +5 to +15. Ensure received items have estimated worth.
- For MAGICAL actions (cast, conjure, channel): spirit/essence delta reflects mana cost.
- artifactReplacements.activeEvent is the validated artifact lane for the current event card. Use it for consequential branching beats that deserve an explicit player-facing event card: quest completion/failure fallout, ambush reveals, relic activation, ritual interruption, debt collection, oath offers, etc. Provide 2-4 meaningful choices. choice.input must be a complete action sentence the player can take.
- If state.activeEvent is present, the player is inside that event. Resolve their choice or free-text against the event details already in context. Set artifactReplacements.activeEvent=null when the event is concluded, defused, accepted, declined, or otherwise no longer needs the event card to remain open.
- Replace the event by setting artifactReplacements.activeEvent to a new event object. Do not emit both a null clear and a new event; the replacement lane is a single authoritative slot.
- Do not stack multiple simultaneous events. If state.activeEvent exists, prefer resolving it instead of spawning another.
- The roll (1–20) represents fortune. Factor it into all contested/risky outcomes.
- meta.cacheable: true ONLY for deterministic, non-contested actions (examine static object, read sign, take item, drop item).
- meta.category: "attack" ONLY when player explicitly attacks an NPC. "ability" for named ability use.
- Mods MUST include duration (minutes). Min 15, typical 30–90. No permanent stat changes.
- Modifier label forging contract: AI-first semantic chip text. Prefer concise labels with one leading emoji when clear (examples: "🔥 BURNING", "🛡️ WARD", "⚡ STUNNED"). JS may fallback-map plain labels, but AI-authored labels are preferred.
- inventoryRemove: exact name strings matching items in state.player.inventory[].name (acting entity inventory snapshot).
- Write the function body compactly. Must parse as: new Function('state','roll','ctx','return ('+logicFn+')(state,roll,ctx)')`
export const ABILITY_FORGE_SYSTEM = `You are the Ability Forge — an architect of unique skills.
Write a deterministic math function for a specific named ability.
Output strict JSON:
{
  "logicFn": "(state, roll, ctx) => { return { successLevel: number, updates: { vitality?: number, spirit?: number, essence?: number, timeCost: number, mods?: { [statKey]: { val: number, label: string, source: string } }, inventoryAdd?: Array<string|{ name: string, worth?: number, desc?: string, icon?: string, actions?: [{ id: string, label: string, inputTemplate: string, mode: 'test'|'do', category: 'examine'|'take'|'use'|'attack'|'move'|'interact'|'ability'|'other', when?: 'inventory'|'room'|'any', targetHint?: 'none'|'self'|'active_npc', execution?: 'shared'|'item_forge' }] }>, inventoryRemove?: string[], roomItemsAdd?: Array<string|{ name: string, worth?: number, desc?: string, icon?: string, actions?: [{ id: string, label: string, inputTemplate: string, mode: 'test'|'do', category: 'examine'|'take'|'use'|'attack'|'move'|'interact'|'ability'|'other', when?: 'inventory'|'room'|'any', targetHint?: 'none'|'self'|'active_npc', execution?: 'shared'|'item_forge' }] }>, roomItemsRemove?: string[], clockUpdates?: { [clockKey]: number }, npcRelationshipUpdates?: { [npcName]: number }, npcMemoryAdd?: { [npcName]: string } } }; }"
}
Rules:
- successLevel: 0=fail, 1=partial, 2=full success.
- The function MUST respect the ability's description as the source of truth.
- Stats: ${STAT_KEYS}. Read as state.player.attributes[key].val.
- Skills: read as state.player.skills[].
- Skill scale is logarithmic: level ${HUMAN_AVG_WALKING_SKILL_LEVEL} is average human walking, +${SKILL_LEVEL_DOUBLING_DELTA} levels is ~2x proficiency.
- If an ability clearly maps to a skill, include that skill's influence in success thresholds and effect strength.
- timeCost: typically 5–15 minutes.
- For offensive/combat abilities: vitalityDelta = 0 (player attacks, NPC counterattacks). Narrative handles NPC damage.
- For healing/buffing abilities: apply positive vitality/spirit deltas or beneficial modifiers.
- For resource costs: use negative spirit/essence delta.
- Mods MUST include duration (minutes). Suggest: cooldown * 1.5 as a baseline. Min 15 min.
- Modifier label forging contract: AI-first semantic chip text. Prefer concise labels with one leading emoji when clear (examples: "🔥 BURNING", "🛡️ WARD", "⚡ STUNNED"). JS may fallback-map plain labels, but AI-authored labels are preferred.
- The roll (1–20) represents fortune. Factor it into the successLevel and outcome.
- ctx is the execution context. Use ctx.action.targets when the ability clearly names or implies a target.
- Write the function body compactly. No comments. Must parse as: new Function('state','roll','ctx','return ('+logicFn+')(state,roll,ctx)')`

export const ITEM_ACTION_HINTS_SYSTEM = `You classify what kind of interaction affordances an item naturally suggests.
Output strict JSON only:
{
  "readable": boolean,
  "presentable": boolean,
  "selfUse": boolean,
  "targetUse": boolean,
  "lightSource": boolean
}
Rules:
- Judge semantics, not spelling patterns.
- Base your answer on the item identity plus optional description/context.
- Use true only when the affordance is a natural, likely player action for that item.
- readable: texts, maps, ledgers, notes, codices, manuals, runic tablets, similar reading surfaces.
- presentable: keys, seals, badges, proofs, writs, relics, tokens, artifacts, items commonly shown as evidence or credentials.
- selfUse: medicines, draughts, bandages, salves, remedies, kits, restorative consumables used on the actor.
- targetUse: thrown/offensive/applied substances or devices typically used on another entity or target.
- lightSource: torches, lanterns, lamps, candles, flares, or other obvious lighting implements.
- If uncertain, return false.
- Never include prose or explanations.`

export function buildItemActionHintsPrompt(
  itemName: string,
  options: {
    desc?: string
    location?: 'inventory' | 'room'
    equipped?: boolean
    worldSeed?: string
  } = {},
): string {
  return `Item name: ${JSON.stringify(String(itemName ?? '').trim())}
Description: ${JSON.stringify(String(options.desc ?? '').trim() || 'none')}
Location: ${JSON.stringify(String(options.location ?? 'inventory'))}
Equipped: ${options.equipped === true ? 'yes' : 'no'}
World seed hint: ${JSON.stringify(String(options.worldSeed ?? '').trim().slice(0, 160) || 'none')}`
}

export function buildAbilityForgePrompt(ability: Ability, state: GameState): string {
  const a = state.player.attributes
  const attrGuidance = Object.entries(a)
    .map(([key, attr]) => `${key}=${attr.fullName ?? attr.desc}: ${attr.aiGuidance ?? attr.desc}`)
    .join(' | ')
  const skills = state.player.skills
    .slice(0, 12)
    .map(s => `${s.name}:${s.level}`)
    .join(', ') || 'none'
  return `Ability: "${ability.name}" — ${ability.desc}
Cooldown: ${ability.cooldown} minutes
Current Vitals: VIT=${formatVitalValue(state.player.vitals, 'vitality')} SPR=${formatVitalValue(state.player.vitals, 'spirit')} ESS=${formatVitalValue(state.player.vitals, 'essence')}
Attributes: STR=${a.STR.val} DEX=${a.DEX.val} CON=${a.CON.val} INT=${a.INT.val} WIS=${a.WIS.val} CHA=${a.CHA.val} PER=${a.PER.val} LCK=${a.LCK.val} WIL=${a.WIL.val} FOR=${a.FOR.val} AGI=${a.AGI.val} PRS=${a.PRS.val}
Attribute guidance: ${attrGuidance}
Skills: ${skills}
Skill scale note: average walking=${HUMAN_AVG_WALKING_SKILL_LEVEL}, +${SKILL_LEVEL_DOUBLING_DELTA} levels ~= 2x proficiency
Inventory: ${state.player.inventory.map(i => i.equipped ? `${i.name}[E]` : i.name).join(', ') || 'empty'}`
}


// ---- Hybrid narrator (single call: prose + === + JSON) ----
// The hybrid format lets the AI stream prose naturally, then append structured
// data after a "===" separator. This replaces the old two-call approach.

export function buildNarratorHybridSystem(): string {
  return `You are the Narrator. Write concise consequence-first prose, then append structured data.

CRITICAL: Your response MUST contain exactly one === separator on its own line. Nothing before the prose. Nothing after the JSON.

[1-2 short sentences by default. 3-4 sentences only for special events. Present-tense. No game mechanics. No dice. No JSON.]
===
{"suggestions":[{"short":"VERB","full":"Full action sentence the player can speak."}],"newRoom":null,"atlasHints":{"places":[],"paths":[{"from":"x_y_z","to":"x_y_z","risk":0.2}]}}

Rules for prose:
- Default is TL;DR: front-load the direct consequence in the first sentence.
- Keep ordinary outcomes compact (target: <=45 words total).
- Expand to 3-4 sentences only when prompt says Narrative mode SPECIAL or special reasons are provided.
- Maintain immersive tone but avoid decorative verbosity.
- Do NOT restate or echo the player's action. Narrate the results and their consequences.
- Use at most one relevant emoji when it adds signal. Avoid emoji spam.
- Use markdown emphasis sparingly.
- In active combat: describe the exchange of blows, enemy reactions, tension.
- On victory: describe the enemy falling, the aftermath.
- On defeat or critical wounds (successLevel=0): describe consequences, pain, and obstacles vividly — make failure feel meaningful.
- On critical failure: be specific about what went wrong.
- Companions marked [AWAY] are separated from the player and NOT present in the scene. Do not narrate them reacting or speaking.
- Present companions may react with a brief verbal or emotional response (1 sentence max) but must NOT take independent actions.
- Never mention dice rolls, success levels, or stat numbers.
- CRITICAL: Narrate only the CONSEQUENCES of the player's action. Do NOT decide new actions for the player or companions.
- Pronoun mapping is strict: "you/your" always refers to the player character. Any "I/me/my" in the action input also refers to the player unless actor context explicitly says companion action.
- Never refer to the main character as "the player", "player character", or other third-person meta labels in player-facing prose. Use second person ("you/your") instead.
- Use the canonical player name from prompt context. Never invent or substitute a different player name.
- Keep named references grounded: use only entities present in context unless the action explicitly introduces a new entity.
- Do NOT move the player to a new room unless the action explicitly involves movement AND the engine result supports it.
- Player agency is sacred: only narrate what the player intended, not what you think they should do next.

Rules for JSON (after ===):
- suggestions: 3–4 items. short = SCREAMING_CASE ≤10 chars. full = a complete action sentence the player would speak.
  In combat always include ATTACK and FLEE options.
- newRoom: include ONLY if the action logically moves the player to a new location.
  If included: {"name":string,"desc":string,"xOff":integer,"yOff":integer,"zOff":integer,"items":[],"npcs":[]}
  xOff/yOff are cardinal horizontal offsets (no diagonals). zOff is vertical (1=up, -1=down). Set to 0 if no change.
- newRoom.npcs must contain only in-world diegetic characters. Never emit meta/system roles (Narrator, Supervisor, Game Master, Logic Forge, Reality Forge, etc.) as NPCs.
  atlasHints: include only when the action reveals concrete map intelligence with exact grid coordinates.
  Format:
  {
    "places":[{"key":"x_y_z","name":"Place Name","desc":"optional short note"}],
    "paths":[{"from":"x_y_z","to":"x_y_z","risk":number}]
  }
  Use absolute keys. Only add direct neighbor paths (cardinal horizontal or vertical one-step links).
  risk: a float from 0.0 (safe) to 1.0 (lethal). Scale risk by narrative distance, environmental hazards, or known dangers.
  If uncertain, omit atlasHints.
- Output valid JSON. Nothing after the closing brace.`
}

// Legacy prose-only system (kept for fallback / non-streaming use)
export function buildNarratorProseSystem(): string {
  return `You are the Narrator. Receive computed game results and write concise consequence prose.
Rules:
- Output only the prose narrative. Do not include any other text or JSON.
- Default prose should be 1-2 short sentences (TL;DR). Use 3-4 only for major/special events.
- Present-tense. Immersive but concise.
- Use at most one emoji if it adds clarity/mood. Avoid decorative clutter.
- In active combat: describe the exchange of blows, enemy reactions, tension. Reference combat log if provided.
- On victory: describe the enemy falling, the aftermath.
- On defeat/critical state: describe desperation, near-death vividly.
- Pronoun mapping is strict: "you/your" refers only to the player character.
- Never use third-person meta labels like "the player" or "player character" in prose.
- Never mention dice rolls or game mechanics in prose.`
}

export function buildNarratorSuggestionsSystem(): string {
  return `You are the Narrator's assistant. You receive the game state and provide gameplay suggestions.
Output strict JSON:
{
  "suggestions": [ { "short": string, "full": string } ],
  "newRoom"?: { "name": string, "desc": string, "xOff": number, "yOff": number, "zOff": number, "items": string[], "npcs": [ { "name": string, "identity": string, "lore": string } ] },
  "atlasHints"?: { "places"?: [{ "key": "x_y_z", "name": string, "desc"?: string }], "paths"?: [{ "from": "x_y_z", "to": "x_y_z", "risk"?: number }] }
}
Rules:
- short: SCREAMING_CASE, max 10 chars. Chip label.
- full: 1 complete action sentence the player could speak.
- 3–4 suggestions appropriate to the current situation (in combat: include ATTACK, FLEE options).
- newRoom: only if the action logically moves the player to a new location. xOff/yOff/zOff are integer coordinate offsets.
- atlasHints: include only when exact coordinates are known. Use key format "x_y_z" and direct-neighbor paths only.
- risk: optional float 0.0–1.0 representing travel danger.`
}

function buildActionEnvelopeContext(actionEnvelope?: ActionEnvelope): string {
  if (!actionEnvelope) return ''
  const actor = `${actionEnvelope.actor.kind}:${actionEnvelope.actor.label}`
  const targets = actionEnvelope.targets.length
    ? actionEnvelope.targets.map(target => `${target.kind}:${target.label}`).join(', ')
    : 'none'
  const tool = actionEnvelope.tool ? `${actionEnvelope.tool.kind}:${actionEnvelope.tool.label}` : 'none'
  const args = actionEnvelope.args && Object.keys(actionEnvelope.args).length
    ? Object.entries(actionEnvelope.args).map(([key, value]) => `${key}=${value}`).join(', ')
    : 'none'
  const tags = actionEnvelope.tags?.length ? actionEnvelope.tags.join(', ') : 'none'
  return `
Action envelope: source=${actionEnvelope.source} mode=${actionEnvelope.mode} actor=${actor} tool=${tool} targets=${targets} args=${args} tags=${tags}`
}

export function buildLogicForgePrompt(state: GameState, input: string, actorName?: string, actionEnvelope?: ActionEnvelope): string {
  const a = state.player.attributes
  const attrGuidance = Object.entries(a)
    .map(([key, attr]) => `${key}=${attr.fullName ?? attr.desc}: ${attr.aiGuidance ?? attr.desc}`)
    .slice(0, 12).join(' | ')
  const skillCtx = state.player.skills.length
    ? `
Skills: ${state.player.skills.slice(0, 6).map(s => `${s.name}:${s.level}`).join(', ')}`
    : ''
  // L-1.8: Context diet — strip combat from non-combat actions (examine, take, move, interact, other)
  const isCombatAction = /^(attack|use ability:|cast |fight |hit |strike |defend)/i.test(input)
  const combatCtx = state.combat?.status === CombatStatus.active && isCombatAction
    ? `
Active combat: Round ${state.combat.round} vs ${state.combat.npc.name} (${state.combat.npc.identity})
Turn queue: ${(state.combat.turnQueue ?? state.combat.initiative ?? []).slice(0, 2).join(' -> ')}
Foes: ${(state.combat.foes ?? [{ name: state.combat.npc.name, vitality: state.combat.npcVitality }]).slice(0, 2).map(f => `${f.name}(${f.vitality}%)`).join(', ')}`
    : ''
  const npcCtx = state.currentRoom.npcs.length
    ? `
Room NPCs: ${buildNpcSummary(state.currentRoom.npcs.slice(0, 2))}`
    : ''
  const traitCtx = state.player.traits.length
    ? `
Traits: ${state.player.traits.slice(0, 3).map(t => t.name).join(', ')}`
    : ''
  const flagsCtx = state.currentRoom.flags && Object.keys(state.currentRoom.flags).length > 0
    ? `
Room Flags: ${buildFlagSummary(state.currentRoom.flags, 4)}`
    : ''
  const questCtx = buildQuestContext(state)
  const clockCtx = buildClockContext(state)
  const notebookCtx = buildNotebookContext(state)
  const worldLogCtx = buildWorldLogContext(state)
  const recentActionsCtx = buildRecentActionsContext(state)
  const roomEntityCtx = buildRoomEntityContext(state)
  const localAtlasCtx = buildLocalAtlasContext(state)
  const modifierCtx = Object.entries(state.player.modifiers ?? {})
    .flatMap(([key, mods]) => (mods ?? []).map(mod => `${key}:${mod.label}:${mod.val}`))
    .slice(0, 6)
  const activeModsCtx = modifierCtx.length
    ? `
Active modifiers: ${modifierCtx.join(', ')}`
    : ''
  const activeEventCtx = buildActiveEventContext(state)
  const companionsCtx = (state.companions ?? []).length > 0
    ? `
Companions present: ${(state.companions ?? []).slice(0, 2).map(c => `${c.name}(${c.vitals?.vitality || 0}%)`).join(', ')}`
    : ''
  const actorCtx = actorName
    ? `
Actor: ${actorName} (companion action)`
    : `
Actor: ${state.player.name || state.player.identity || 'Player'} (player action)`
  const actorCompanion = actorName
    ? (state.companions ?? []).find(c => String(c.name ?? '').trim().toLowerCase() === String(actorName).trim().toLowerCase())
    : null
  const actorCompanionSkillCtx = actorCompanion && Array.isArray(actorCompanion.skills) && actorCompanion.skills.length
    ? actorCompanion.skills.slice(0, 4).map(s => `${s.name}:${s.level}`).join(', ')
    : 'none'
  const actorCompanionInvCtx = actorCompanion
    ? buildInventorySummary(actorCompanion.inventory ?? [], 4)
    : ''
  const actorCompanionCtx = actorCompanion
    ? `
Actor companion vitals: VIT=${formatVitalValue(actorCompanion.vitals ?? {}, 'vitality')} SPR=${formatVitalValue(actorCompanion.vitals ?? {}, 'spirit')} ESS=${formatVitalValue(actorCompanion.vitals ?? {}, 'essence')}
Actor companion skills: ${actorCompanionSkillCtx}
Actor companion inventory: ${actorCompanionInvCtx}`
    : ''
  const knownNames = [
    state.player.name || state.player.identity || 'Player',
    ...(state.companions ?? []).map(c => c.name),
    ...(state.currentRoom.npcs ?? []).map(n => n.name),
  ]
    .map(v => String(v || '').trim())
    .filter(Boolean)
  const knownNameList = [...new Set(knownNames)].slice(0, 32).join(', ') || 'none'
  const actionEnvelopeCtx = buildActionEnvelopeContext(actionEnvelope)

  return `Action: "${input.replace(/^Use ability: /i, '')}"
${actorCtx}
Perspective: Pronouns "I/me/my/myself" in Action refer to the Actor above.
${actorCompanionCtx}
${actionEnvelopeCtx}
Player canonical name: ${state.player.name || state.player.identity || 'Player'} (do not rename).
Known entities: ${knownNameList}
Vitals: VIT=${formatVitalValue(state.player.vitals, 'vitality')} SPR=${formatVitalValue(state.player.vitals, 'spirit')} ESS=${formatVitalValue(state.player.vitals, 'essence')}
Attributes: STR=${a.STR.val} DEX=${a.DEX.val} CON=${a.CON.val} INT=${a.INT.val} WIS=${a.WIS.val} CHA=${a.CHA.val} PER=${a.PER.val} LCK=${a.LCK.val} WIL=${a.WIL.val} FOR=${a.FOR.val} AGI=${a.AGI.val} PRS=${a.PRS.val}${traitCtx}${skillCtx}
Attribute guidance: ${attrGuidance}
Skill scale note: average walking=${HUMAN_AVG_WALKING_SKILL_LEVEL}, +${SKILL_LEVEL_DOUBLING_DELTA} levels ~= 2x proficiency
Inventory: ${buildInventorySummary(state.player.inventory, 20)}
Room items: ${buildCompactValueList(state.currentRoom.items, 18, 3) || 'none'}${flagsCtx}${roomEntityCtx}${localAtlasCtx}${clockCtx}${questCtx}${notebookCtx}${worldLogCtx}${recentActionsCtx}${npcCtx}${companionsCtx}${activeModsCtx}${activeEventCtx}${combatCtx}`
}

function compactPromptLine(value: string, maxLen = 88): string {
  const text = collapsePromptWhitespace(String(value ?? ''))
  if (!text) return ''
  if (text.length <= maxLen) return text
  return `${text.slice(0, maxLen - 3)}...`
}

function collapsePromptWhitespace(value: string): string {
  let out = ''
  let pendingSpace = false
  for (let i = 0; i < value.length; i++) {
    const ch = value[i]
    const isWhitespace = ch === ' ' || ch === '\n' || ch === '\r' || ch === '\t' || ch === '\f' || ch === '\v'
    if (isWhitespace) {
      if (out) pendingSpace = true
      continue
    }
    if (pendingSpace) {
      out += ' '
      pendingSpace = false
    }
    out += ch
  }
  return out
}

function stripHtmlToPromptText(value: string): string {
  let out = ''
  let insideTag = false
  let pendingSpace = false
  for (let i = 0; i < value.length; i++) {
    const ch = value[i]
    if (ch === '<') {
      insideTag = true
      if (out) pendingSpace = true
      continue
    }
    if (insideTag) {
      if (ch === '>') insideTag = false
      continue
    }
    const isWhitespace = ch === ' ' || ch === '\n' || ch === '\r' || ch === '\t' || ch === '\f' || ch === '\v'
    if (isWhitespace) {
      if (out) pendingSpace = true
      continue
    }
    if (pendingSpace) {
      out += ' '
      pendingSpace = false
    }
    out += ch
  }
  return out
}

function takeRecent<T>(items: T[], limit: number): T[] {
  return items.length <= limit ? items : items.slice(-limit)
}

function valueToPromptLabel(value: unknown): string {
  if (typeof value === 'string') return value
  if (value && typeof value === 'object' && typeof (value as { name?: unknown }).name === 'string') {
    return String((value as { name: string }).name)
  }
  return String(value ?? '')
}

function buildCompactValueList(values: Array<unknown>, itemMaxLen: number, limit: number, joiner = ', '): string {
  return values
    .map(value => compactPromptLine(valueToPromptLabel(value), itemMaxLen))
    .filter(Boolean)
    .slice(0, limit)
    .join(joiner)
}

function buildFlagSummary(flags: Record<string, unknown> | undefined | null, limit = 12): string {
  if (!flags) return ''
  return Object.entries(flags)
    .slice(0, limit)
    .map(([key, value]) => {
      if (value == null) return compactPromptLine(key, 20)
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return compactPromptLine(`${key}=${String(value)}`, 28)
      }
      return compactPromptLine(key, 20)
    })
    .filter(Boolean)
    .join(' | ')
}

function buildInventorySummary(items: Array<{ name?: string; equipped?: boolean }> | undefined | null, limit = 8): string {
  if (!items?.length) return 'empty'
  // Consolidate duplicates: group by name, count occurrences
  const grouped = new Map<string, { name: string; count: number; equipped: boolean }>()
  for (const item of items) {
    const name = item?.name ?? 'Unknown'
    const existing = grouped.get(name.toLowerCase())
    if (existing) {
      existing.count++
    } else {
      grouped.set(name.toLowerCase(), { name, count: 1, equipped: item?.equipped ?? false })
    }
  }
  // Format as "3x Item Name" or "Item Name[E]" if equipped
  const result = Array.from(grouped.values())
    .slice(0, limit)
    .map(entry => {
      const equipped = entry.equipped ? '[E]' : ''
      const prefix = entry.count > 1 ? `${entry.count}x ` : ''
      return compactPromptLine(`${prefix}${entry.name}${equipped}`, 18)
    })
    .filter(Boolean)
    .join(', ') || 'empty'
  return result
}

function buildNpcSummary(npcs: NPC[] | undefined | null, limit = 4): string {
  if (!npcs?.length) return ''
  return npcs
    .slice(0, limit)
    .map(npc => {
      const memory = takeRecent(npc.memory ?? [], 2).map(entry => compactPromptLine(entry, 24)).filter(Boolean).join('; ')
      const lore = compactPromptLine(npc.lore ?? '', 44)
      const parts = [
        compactPromptLine(npc.name ?? '', 22),
        compactPromptLine(`[${npc.identity ?? 'Unknown'}]`, 28),
        `rel:${npc.relationship ?? 0}`,
      ]
      if (memory) parts.push(`mem:${memory}`)
      if (lore) parts.push(lore)
      return parts.join(' ')
    })
    .filter(Boolean)
    .join('; ')
}

function buildCompanionSummary(state: GameState, limit = 3): string {
  const companions = state.companions ?? []
  if (!companions.length) return ''
  return companions
    .slice(0, limit)
    .map(companion => {
      const present = isCompanionPresent(companion, state.player.location)
      const vitals = `vit:${formatVitalValue(companion.vitals, 'vitality')} spr:${formatVitalValue(companion.vitals, 'spirit')} ess:${formatVitalValue(companion.vitals, 'essence')}`
      const inventory = buildInventorySummary(companion.inventory ?? [], 4)
      const presence = present ? '' : ' [AWAY]'
      return `${compactPromptLine(companion.name ?? '', 18)} [${compactPromptLine(companion.identity ?? '', 20)}]${presence} ${vitals} inv:${inventory}`
    })
    .join('; ')
}

function buildAtlasNodeSummary(state: GameState, limit = 10): string {
  const entries = Object.entries(state.atlas.nodes ?? {})
  if (!entries.length) return 'none'
  return entries
    .slice(0, limit)
    .map(([key, node]) => `${compactPromptLine(key, 12)}:${compactPromptLine(node?.name ?? 'Unknown', 18)}`)
    .join('; ') || 'none'
}

function buildQuestContext(state: GameState): string {
  const quests = Array.isArray(state.journal) ? state.journal : []
  if (!quests.length) return ''
  const active = quests
    .filter(q => q?.status === 'active')
    .slice(0, 4)
    .map(q => compactPromptLine(`${q.title}:${q.status}`))
    .filter(Boolean)
  const pending = quests
    .filter(q => q?.status === 'potential' || q?.status === 'dormant')
    .slice(0, 3)
    .map(q => compactPromptLine(`${q.title}:${q.status}`))
    .filter(Boolean)
  const bits = []
  if (active.length) bits.push(`active[${active.join(' | ')}]`)
  if (pending.length) bits.push(`pending[${pending.join(' | ')}]`)
  if (!bits.length) return ''
  return `
Quest context: ${bits.join(' ')}`
}

function buildClockContext(state: GameState): string {
  const roomClocks = Array.isArray(state.currentRoom?.clocks) ? state.currentRoom.clocks.slice(0, 4) : []
  const worldClocks = Array.isArray(state.world?.clocks) ? state.world.clocks.slice(0, 4) : []
  const roomSummary = roomClocks
    .map(clock => `room:${clock.id}=${compactPromptLine(formatProgressClock(clock), 56)}`)
    .filter(Boolean)
  const worldSummary = worldClocks
    .map(clock => `world:${clock.id}=${compactPromptLine(formatProgressClock(clock), 56)}`)
    .filter(Boolean)
  const bits = []
  if (roomSummary.length) bits.push(`room[${roomSummary.join(' | ')}]`)
  if (worldSummary.length) bits.push(`world[${worldSummary.join(' | ')}]`)
  if (!bits.length) return ''
  return `
Active clocks: ${bits.join(' ')}`
}

function buildNotebookContext(state: GameState): string {
  const notes = Array.isArray(state.notebook) ? state.notebook : []
  if (!notes.length) return ''
  const recent = notes
    .slice(0, 4)
    .map(note => compactPromptLine(String(note ?? '').replace(/^\[[A-Z]+\]\s*/i, '')))
    .filter(Boolean)
  if (!recent.length) return ''
  return `
Notebook facts: ${recent.join(' | ')}`
}

function buildWorldLogContext(state: GameState): string {
  const entries = Array.isArray(state.worldLog) ? state.worldLog : []
  if (!entries.length) return ''
  const recent = takeRecent(entries, 3)
    .map(entry => {
      if (typeof entry === 'string') return compactPromptLine(entry, 72)
      const text = compactPromptLine(String(entry?.text ?? ''), 64)
      const stamp = compactPromptLine(String(entry?.time ?? entry?.type ?? '').trim(), 20)
      if (!text && !stamp) return ''
      return stamp && text ? `${stamp}:${text}` : (text || stamp)
    })
    .filter(Boolean)
  if (!recent.length) return ''
  return `
World log cues: ${recent.join(' | ')}`
}

function buildRecentActionsContext(state: GameState): string {
  const entries = Array.isArray(state.history) ? state.history : []
  if (!entries.length) return ''
  const recent = entries
    .slice(-2)
    .map(entry => {
      const location = compactPromptLine(String(entry?.location ?? 'Unknown'), 18)
      const compactText = compactPromptLine(stripHtmlToPromptText(String(entry?.text ?? '')), 64)
      if (!compactText) return ''
      return `${location}:${compactText}`
    })
    .filter(Boolean)
  if (!recent.length) return ''
  return `
Recent actions: ${recent.join(' | ')}`
}

function buildRoomEntityContext(state: GameState): string {
  const room = state.currentRoom
  if (!room) return ''
  const npcNames = (room.npcs ?? []).slice(0, 4).map(n => compactPromptLine(String(n?.name ?? ''), 22)).filter(Boolean)
  const itemNames = (room.items ?? []).slice(0, 5).map(item => compactPromptLine(valueToPromptLabel(item), 18)).filter(Boolean)
  const flagNames = Object.keys(room.flags ?? {}).slice(0, 4)
  const parts = []
  if (npcNames.length) parts.push(`npcs[${npcNames.join(', ')}]`)
  if (itemNames.length) parts.push(`items[${itemNames.join(', ')}]`)
  if (flagNames.length) parts.push(`flags[${flagNames.join(', ')}]`)
  if (!parts.length) return ''
  return `
Room context: ${parts.join(' ')}`
}

function buildLocalAtlasContext(state: GameState): string {
  const edges = state?.atlas?.edges ?? []
  const nodes = state?.atlas?.nodes ?? {}
  const x = state?.player?.location?.x ?? 0
  const y = state?.player?.location?.y ?? 0
  const exits: string[] = []
  for (const edge of edges) {
    let nx = null
    let ny = null
    if (edge.x1 === x && edge.y1 === y) {
      nx = edge.x2
      ny = edge.y2
    } else if (edge.x2 === x && edge.y2 === y) {
      nx = edge.x1
      ny = edge.y1
    }
    if (nx == null || ny == null) continue
    const dx = nx - x
    const dy = ny - y
    let direction = ''
    if (dx === 0 && dy === 1) direction = 'north'
    else if (dx === 1 && dy === 0) direction = 'east'
    else if (dx === 0 && dy === -1) direction = 'south'
    else if (dx === -1 && dy === 0) direction = 'west'
    if (!direction) continue
    const key = `${nx}_${ny}`
    const nodeName = compactPromptLine(String(nodes[key]?.name ?? key), 24)
    exits.push(`${direction}->${nodeName}`)
  }
  if (!exits.length) return ''
  return `
Known local exits: ${[...new Set(exits)].slice(0, 4).join(' | ')}`
}

// Compact recent-event context for narrator continuity.
// Takes the first sentence (≤80 chars) of the last 2 history entries.
// Gives the narrator awareness of what just happened without bloating the prompt.
function buildRecentContext(state: GameState): string {
  const h = state.history
  if (h.length < 2) return ''
  const snippets = h.slice(-2).map(e => compactPromptLine(e.text.split('.')[0], 80))
  return `
Recent: ${snippets.join('; ')}`
}

function buildActiveEventContext(state: GameState): string {
  const event = state.activeEvent
  if (!event) return ''
  const choiceSummary = (event.choices ?? [])
    .slice(0, 4)
    .map(choice => `${compactPromptLine(choice.label, 20)}=>${compactPromptLine(choice.input, 40)}`)
    .join(' | ')
  const imageSeed = compactPromptLine(String(event.imageSeed ?? ''), 40)
  return `
Active event: ${compactPromptLine(event.title, 48)}
Event desc: ${compactPromptLine(event.desc, 180)}
Event choices: ${choiceSummary || 'none'}
Event art hint: kind=${event.imageKind ?? 'scene'} seed=${imageSeed || 'none'}
Event rule: this event remains open until you set updates.artifactReplacements.activeEvent=null or explicitly replace it via updates.artifactReplacements.activeEvent.`
}

function inferNarrativeMode(state: GameState, input: string, logicResult: LogicUpdate): { mode: 'COMPACT' | 'SPECIAL'; reasons: string[] } {
  const reasons: string[] = []
  const updates = logicResult?.updates ?? { timeCost: 0 }
  const vitalDelta = Math.abs(Number(updates.vitality ?? 0))
  const spiritDelta = Math.abs(Number(updates.spirit ?? 0))
  const essenceDelta = Math.abs(Number(updates.essence ?? 0))

  if (vitalDelta >= 15 || spiritDelta >= 15 || essenceDelta >= 15) {
    reasons.push('major_vitals_shift')
  }
  if ((updates.questUpdates?.length ?? 0) > 0) {
    reasons.push('quest_state_change')
  }
  if (updates.npcRelationshipUpdates && Object.values(updates.npcRelationshipUpdates).some(v => Math.abs(Number(v ?? 0)) >= 20)) {
    reasons.push('major_relationship_shift')
  }
  if (/\b(betray|ritual|portal|transform|entropy)\b/i.test(input)) {
    reasons.push('high_drama_intent')
  }
  if (state.combat?.status === CombatStatus.active && logicResult.successLevel === 0 && vitalDelta >= 10) {
    reasons.push('combat_crisis')
  }

  return {
    mode: reasons.length ? 'SPECIAL' : 'COMPACT',
    reasons,
  }
}

export function buildNarratorPrompt(
  state: GameState,
  input: string,
  logicResult: LogicUpdate,
  combatSummary?: string,
): string {
  const combatCtx = combatSummary
    ? `
Combat: ${combatSummary}`
    : state.combat?.status === CombatStatus.active
      ? `
Combat round ${state.combat.round} vs ${state.combat.npc.name} (NPC vitality: ~${state.combat.npcVitality}%)`
      : ''
  const npcCtx = state.currentRoom.npcs.length
    ? `
Present: ${state.currentRoom.npcs.slice(0, 4).map(n => `${compactPromptLine(n.name, 18)} (${compactPromptLine(n.identity, 18)})`).join(', ')}`
    : ''
  const flagsCtx = state.currentRoom.flags && Object.keys(state.currentRoom.flags).length > 0
    ? `
Room Flags: ${buildFlagSummary(state.currentRoom.flags)}`
    : ''
  const questCtx = buildQuestContext(state)
  const notebookCtx = buildNotebookContext(state)
  const worldLogCtx = buildWorldLogContext(state)
  const recentActionsCtx = buildRecentActionsContext(state)
  const roomEntityCtx = buildRoomEntityContext(state)
  const localAtlasCtx = buildLocalAtlasContext(state)

  const recentCtx = buildRecentContext(state)
  const activeEventCtx = buildActiveEventContext(state)
  const narrativeMode = inferNarrativeMode(state, input, logicResult)
  const canonicalPlayerName = state.player.name || state.player.identity || 'Player'
  const knownNames = [
    canonicalPlayerName,
    ...(state.companions ?? []).map(c => c.name),
    ...(state.currentRoom.npcs ?? []).map(n => n.name),
  ]
    .map(v => String(v || '').trim())
    .filter(Boolean)
  const knownNameList = [...new Set(knownNames)].slice(0, 32).join(', ') || 'none'

  const playerKey = locationKey(state.player.location)
  const knownAtlasNodes = buildAtlasNodeSummary(state, 10)

  // A-1.40: If logic added/removed items, narrator MUST mention this in prose
  const inventoryChanges = []
  if (logicResult.updates.inventoryAdd?.length) {
    inventoryChanges.push(`Added to inventory: ${logicResult.updates.inventoryAdd.join(', ')}`)
  }
  if (logicResult.updates.inventoryRemove?.length) {
    inventoryChanges.push(`Removed from inventory: ${logicResult.updates.inventoryRemove.join(', ')}`)
  }
  const inventoryMentionRule = inventoryChanges.length
    ? `\nInventory changes MUST be explicitly mentioned in the prose: ${inventoryChanges.join(' | ')}`
    : ''
  const econ = state.world.taxonomy?.economy
  const econCtx = econ ? `\nECONOMY: Currency is ${econ.currencyName} (${econ.currencySymbol}). 1 day of food costs ${econ.rationCost} units.` : ''
  return `Action: "${input}"
Perspective contract: "I/me/my" in Action refers to the player character (${canonicalPlayerName}). "you/your" in prose should refer to that same player.
Prose ban: never call the main character "the player" or "player character" in player-facing prose.
Player canonical name: ${canonicalPlayerName} (never rename this character).
Known entities: ${knownNameList}
Engine result: successLevel=${logicResult.successLevel} timeCost=${logicResult.updates.timeCost}${logicResult.updates.vitality != null ? ` vitalityΔ=${logicResult.updates.vitality}` : ''}${logicResult.updates.spirit != null ? ` spiritΔ=${logicResult.updates.spirit}` : ''}
Narrative mode: ${narrativeMode.mode}
Special reasons: ${narrativeMode.reasons.join(', ') || 'none'}
Location: ${state.currentRoom.name} @ ${playerKey}
Known atlas nodes: ${knownAtlasNodes}
Scene: ${state.currentRoom.desc.slice(0, 200)}${npcCtx}${flagsCtx}${roomEntityCtx}${localAtlasCtx}${questCtx}${notebookCtx}${worldLogCtx}${recentActionsCtx}${recentCtx}
World seed: ${state.world.seed.slice(0, 120)}${econCtx}${activeEventCtx}${combatCtx}${inventoryMentionRule}`
}

export function buildGenesisPrompt(identity: string, bio: string, worldSeed: string): string {
  return `Character: "${identity}"
Bio: "${bio}"
World seed: "${worldSeed || 'A classic fantasy realm with hidden mysteries.'}"`
}

export const CHEAT_SYSTEM = `You are the Game Manager with absolute authority over the game world. Dev mode is active.
The player requests a direct state change. Comply without restrictions.
Output strict JSON matching the LogicForge format:
{
  "logicFn": "(state, roll, ctx) => { return { successLevel: 2, updates: { timeCost: 1, vitality?: number, spirit?: number, essence?: number, inventoryAdd?: Array<string|{ name: string, worth?: number, desc?: string, icon?: string, actions?: [{ id: string, label: string, inputTemplate: string, mode: 'test'|'do', category: 'examine'|'take'|'use'|'attack'|'move'|'interact'|'ability'|'other', when?: 'inventory'|'room'|'any', targetHint?: 'none'|'self'|'active_npc', execution?: 'shared'|'item_forge' }] }>, inventoryRemove?: string[], roomItemsAdd?: Array<string|{ name: string, worth?: number, desc?: string, icon?: string, actions?: [{ id: string, label: string, inputTemplate: string, mode: 'test'|'do', category: 'examine'|'take'|'use'|'attack'|'move'|'interact'|'ability'|'other', when?: 'inventory'|'room'|'any', targetHint?: 'none'|'self'|'active_npc', execution?: 'shared'|'item_forge' }] }>, roomItemsRemove?: string[], roomNpcsAdd?: [{name:string,identity:string,lore:string,sex?:string}], roomFlags?: object, clockUpdates?: { [clockKey]: number }, npcRelationshipUpdates?: object, npcMemoryAdd?: object } }; }"
}
Rules:
- successLevel: always 2 (cheat succeeds).
- timeCost: 1 (instant).
- Apply exactly what the player requested. Be literal and precise.
- If the request mentions adding items, use inventoryAdd. If removing, use inventoryRemove.
- If the request changes vitality/spirit/essence, compute the absolute target value as a delta from current (read state.player.vitals.X for current values).
- If the request changes room items, use roomItemsAdd/Remove.
- If the request creates or spawns a character, NPC, creature, or person into the scene, use roomNpcsAdd with name, identity (brief archetype), lore (1-sentence description), and sex when relevant ("male" or "female" only). NEVER use roomItemsAdd for living beings.
- Never materialize meta/system pseudo-entities as NPCs. Forbidden names include Narrator, Supervisor, Game Master, Logic Forge, Reality Forge, and similar system-role labels.
- Keep the function body compact. Must parse as: new Function('state','roll','ctx','return ('+logicFn+')(state,roll,ctx)')`
export function buildCheatPrompt(prompt: string, state: GameState): string {
  const a = state.player.attributes
  const inv = state.player.inventory.map(i => i.name).join(', ') || 'empty'
  const roomItems = buildCompactValueList(state.currentRoom.items, 18, 8) || 'none'
  return `Cheat request: "${prompt}"
Player vitals: VIT=${formatVitalValue(state.player.vitals, 'vitality')} SPR=${formatVitalValue(state.player.vitals, 'spirit')} ESS=${formatVitalValue(state.player.vitals, 'essence')}
Player inventory: ${inv}
Room items: ${roomItems}
Room: ${state.currentRoom.name}
Attributes: STR=${a.STR.val} DEX=${a.DEX.val} CON=${a.CON.val} INT=${a.INT.val} WIS=${a.WIS.val} CHA=${a.CHA.val}`
}

export const TRAIT_SHIFT_SYSTEM = `An entropy shift has occurred (24 game-hours passed). This character has an entropic/volatile nature — reshuffle their traits.
Output strict JSON: { "traits": [ { "name": string, "desc": string }, ... ] }
Exactly 3 traits. Related to bio but distinct from each other. Keep one entropy-related trait (the one that enables this shift).`
export const TRADE_SEED_SYSTEM = `You are the Trade Merchant. An NPC is considering a trade with the player.
Based on the NPC's identity, lore, and current intentions, suggest what they might offer 
from their hidden stash in exchange for the player's presence or potential items.
VALUE ESTIMATION: Estimate the item's worth in the world's currency based on:
- Basis: 1 day of food (rationCost from prompt) = 1.0 value unit.
- Scarcity: How rare is this item in this world?
Output strict JSON: { "offer": string, "worth": number }
Offer should be a single item name (e.g. "Silver Dagger", "30 Gold Coins", "Ancient Map").`
export function buildTradeSeedPrompt(npc: NPC, state: GameState): string {
  const econ = state.world.taxonomy?.economy
  const econCtx = econ ? `ECONOMY: Currency is ${econ.currencyName} (${econ.currencySymbol}). 1 day of food costs ${econ.rationCost} units.` : ''
  return `NPC: "${npc.name}" (${npc.identity}). Lore: ${npc.lore}
Intentions: ${npc.intentions?.join(', ') || 'none'}
Player: ${state.player.identity}. Bio: ${state.player.bio}
Player inventory: ${state.player.inventory.map(i => i.name).join(', ') || 'empty'}
${econCtx}
What does ${npc.name} offer to start the negotiation?`
}

// ============================================================
// NPC LOGIC FORGE — Archetype system prompts
// Each archetype produces a cached (state, roll, ctx) => NpcLogicUpdate
// function that reads full player state for context-aware attacks.
//
// NpcLogicUpdate shape the function must return:
// {
//   hit: boolean,
//   vitalityDelta: number,       // ≤0
//   spiritDelta?: number,        // ≤0
//   essenceDelta?: number,       // ≤0
//   mods?: { [statKey]: { val: number, label: string, source: string, duration?: number } },
//   inventorySteal?: string,
//   outcome: string
// }
//
// State the function can read:
//   state.player.vitals.{vitality,spirit,essence}  (0-100)
//   state.player.attributes[key].val               (STR,DEX,CON,INT,WIS,CHA,PER,LCK,WIL,FOR,AGI,PRS)
//   state.player.modifiers[key]                    (active mods array)
//   state.player.traits[].name                     (trait names)
//   state.player.inventory                         (InventoryItem[]: [{name:string,equipped:bool}])
//   state.player.inventory.map(i=>i.name)          (string[] — for name-only operations like steal)
// ============================================================

export const NPC_ARCHETYPE = {
  Melee: 'Melee',
  Drain: 'Drain',
  Elemental: 'Elemental',
  Social: 'Social',
  Assassin: 'Assassin',
  Caster: 'Caster',
  Dragon: 'Dragon',
  Generic: 'Generic',
} as const

export type NpcArchetype = typeof NPC_ARCHETYPE[keyof typeof NPC_ARCHETYPE]

const NPC_FN_HEADER = `You are the Logic Forge writing an NPC attack function.
Output strict JSON: { "attackFn": string }
attackFn is a JS arrow function string: (state, roll, ctx) => NpcLogicUpdate
NpcLogicUpdate: { hit:bool, vitalityDelta:number(≤0), spiritDelta?:number(≤0), essenceDelta?:number(≤0), mods?:{[statKey]:{val:number,label:string,source:string,duration?:number}}, inventorySteal?:string, outcome:string }
State: state.player.{vitals,attributes,modifiers,traits,inventory}. roll=1-20.
Write compact JS. No comments. Must parse as: new Function('state','roll','ctx','return ('+attackFn+')(state,roll,ctx)')`
const NPC_ARCHETYPES: Record<NpcArchetype, string> = {
  [NPC_ARCHETYPE.Melee]: `${NPC_FN_HEADER}
Archetype: MELEE (soldier, bandit, warrior, beast).
Rules:
- Miss if roll<=4. Hit deals 5+floor(roll/3) vitality damage, cap 25.
- Read FOR mods: sum them; every +2 FOR mod reduces damage by 1.
- AGI mods or trait names containing "dodge"/"evasion" raise miss threshold to roll<=6.
- Critical (roll=20): add bleed mod CON -1, duration 30.
- vitalityDelta must be <=0.`,

  [NPC_ARCHETYPE.Drain]: `${NPC_FN_HEADER}
Archetype: DRAIN (vampire, wraith, lich, specter, shade).
Rules:
- Miss if roll<=3.
- Check inventory for any item containing "garlic","holy","silver","blessed" → automatic miss, outcome "repelled".
- Check traits for any name containing "divine","ward","protected" → 50% effectiveness.
- Damage split: 30% vitality, 70% essence. Total: 10+floor(roll/2), cap 30.
- On roll>=16: also apply WIL mod -1, duration 60, source "life_drain".
- vitalityDelta and essenceDelta must be <=0.`,

  [NPC_ARCHETYPE.Elemental]: `${NPC_FN_HEADER}
Archetype: ELEMENTAL (fire, ice, lightning, storm, magma, frost).
Rules:
- Infer element from NPC identity string if available.
- Check inventory/traits for resistance keywords matching the element → halve damage.
- Fire: vitalityDelta -8 to -20. On hit apply CON -1 "burning" duration 20.
- Ice: vitalityDelta -6 to -16, spiritDelta -4. On hit apply AGI -2 "slowed" duration 15.
- Lightning: vitalityDelta -10 to -22, spiritDelta -5. Critical: DEX -2 "stunned" duration 5.
- Default to fire if element unclear. Cap total damage at 28.`,

  [NPC_ARCHETYPE.Social]: `${NPC_FN_HEADER}
Archetype: SOCIAL (siren, enchantress, charmer, beguiler, trickster).
Rules:
- Primarily spirit damage. Miss if roll<=5.
- spiritDelta: -(6+floor(roll/3)), cap -20.
- vitalityDelta: 0 to -3 (minor).
- Check WIS+WIL attribute vals: if sum>22 reduce spirit damage by 30%.
- Check traits for "willful","resolute","clear-minded" → reduce by 50%.
- On roll>=14: apply WIL -2 "charmed", duration 45, source "social_charm".`,

  [NPC_ARCHETYPE.Assassin]: `${NPC_FN_HEADER}
Archetype: ASSASSIN (rogue, thief, cutpurse, shadow, stalker).
Rules:
- Miss if roll<=6 (harder to hit, but deadlier).
- Hit: vitalityDelta -(10+floor(roll/2)), cap -28.
- On hit: apply CON -1 "bleeding", duration 45, source "blade_poison".
- Check PER attribute: if val>=13, raise miss threshold to roll<=8.
- On roll>=15 AND inventory not empty: steal one item. Prefer non-equipped items: const stealable=state.player.inventory.filter(i=>typeof i !== 'string' && !i.equipped); const target=stealable.length>0?stealable[stealable.length-1]:state.player.inventory[state.player.inventory.length-1]; inventorySteal=typeof target === 'string' ? target : target?.name.
- inventorySteal = the item name string (NOT the object — use .name).`,

  [NPC_ARCHETYPE.Caster]: `${NPC_FN_HEADER}
Archetype: CASTER (necromancer, warlock, mage, witch, hexer).
Rules:
- Miss if roll<=4.
- Balanced damage: vitalityDelta -(5+floor(roll/4)), spiritDelta -(5+floor(roll/4)). Cap each at -15.
- Check INT+WIS sum: if >22 → 25% chance (roll>=15) to fully resist (hit=false).
- On roll>=16: apply a curse. Pick stat key from [STR,DEX,INT,WIS,CHA] based on roll%5.
  Curse: val -2, label "Hexed", source "caster_curse", duration 90.
- vitalityDelta and spiritDelta must be <=0.`,

  [NPC_ARCHETYPE.Dragon]: `${NPC_FN_HEADER}
Archetype: DRAGON (dragon, wyvern, drake, wyrm, ancient beast).
Rules:
- Only critical fumble (roll=1) misses.
- Base: vitalityDelta -(15+floor(roll/2)), cap -40.
- Check inventory for "dragonbane","dragon scale","scale armor" → halve damage.
- On hit: always apply WIL -2 "dragon_fear", duration 60, source "dragon_fear".
- On roll>=18: also essenceDelta -10 (breath sears the soul).
- vitalityDelta must be <=0.`,

  [NPC_ARCHETYPE.Generic]: `${NPC_FN_HEADER}
Archetype: GENERIC — infer attack style from NPC identity/lore.
Rules:
- Design the function to match the NPC archetype (provided in user prompt).
- Miss if roll<=5 by default; adjust for archetype.
- Choose appropriate vitality/spirit/essence split based on NPC type.
- Apply a contextually appropriate status effect mod if roll>=15.
- Cap total damage at 30. vitalityDelta <=0.`,
}
export function detectNpcArchetype(identity: string, taxonomy?: import("@anyquest/shared-core").WorldTaxonomy): NpcArchetype {
  if (taxonomy) {
    for (const { pattern, archetype } of taxonomy.combatNpcArchetypes) {
      try {
        if (new RegExp(pattern, 'i').test(identity)) {
          const upper = archetype.toUpperCase()
          if (upper in NPC_ARCHETYPE) return NPC_ARCHETYPE[upper as keyof typeof NPC_ARCHETYPE]
        }
      } catch { continue; }
    }
  }
  const s = identity.toLowerCase()
  if (/vampire|wraith|lich|specter|shade|leech|shadow/.test(s)) return NPC_ARCHETYPE.Drain
  if (/\b(fire|flame)\b|ember|inferno|magma|ice|frost|blizzard|lightning|storm|electric/.test(s)) return NPC_ARCHETYPE.Elemental
  if (/siren|enchantress|charmer|beguiler|succubus|incubus/.test(s)) return NPC_ARCHETYPE.Social
  if (/assassin|rogue|thief|cutpurse|stalker|shadow blade/.test(s)) return NPC_ARCHETYPE.Assassin
  if (/necromancer|warlock|witch|mage|wizard|hexer|sorcerer/.test(s)) return NPC_ARCHETYPE.Caster
  if (/dragon|wyvern|drake|wyrm/.test(s)) return NPC_ARCHETYPE.Dragon
  return NPC_ARCHETYPE.Melee
}

export function getNpcAttackSystem(archetype: NpcArchetype): string {
  return NPC_ARCHETYPES[archetype]
}

export function buildNpcAttackPrompt(npc: { identity: string; lore: string }, state: GameState): string {
  // Compact player context — only what's needed for resistance checks
  const activeMods = Object.entries(state.player.modifiers)
    .flatMap(([k, mods]) => (mods ?? []).map(m => `${k}:${m.val}(${m.label})`))
    .join(', ') || 'none'
  const traitNames = state.player.traits.map(t => t.name).join(', ') || 'none'
  const relevantItems = state.player.inventory
    .filter(i => /garlic|holy|silver|blessed|resist|shield|armor|scale|dragon/i.test(i.name))
    .map(i => i.name)
    .join(', ') || 'none'
  const playerLabel = String(state.player.name || state.player.identity || 'Player').trim() || 'Player'
  return `NPC: "${npc.identity}" — ${npc.lore.slice(0, 80)}
Attack context: actor=npc:${npc.identity} target=player:${playerLabel} room=${state.currentRoom.name} time=${formatDay(state.world.clock)}, ${formatClock(state.world.clock)}
Player vitals: VIT=${formatVitalValue(state.player.vitals, 'vitality')} SPR=${formatVitalValue(state.player.vitals, 'spirit')} ESS=${formatVitalValue(state.player.vitals, 'essence')}
Player key attrs: FOR=${state.player.attributes.FOR.val} AGI=${state.player.attributes.AGI.val} WIS=${state.player.attributes.WIS.val} WIL=${state.player.attributes.WIL.val} PER=${state.player.attributes.PER.val}
Active mods: ${activeMods}
Traits: ${traitNames}
Relevant inventory: ${relevantItems}`
}

// ============================================================
// WORLD SIMULATION PROMPTS
// ============================================================

export const WORLD_TICK_SYSTEM = `You are the World Simulation Engine. The world advances while the player acts.
You manage clocks, NPC schedules, encounters, and world events.
RULES:
1. Advance clocks if their conditions are met.
2. Generate logical encounters or room changes based on the player's action and time passed.
3. NPC NAME GENERATION: When generating new NPCs, use a thematic name pool strictly tied to the world seed's lore. Never use generic names like "Guard" or "Thief" as their actual name.
4. NO NAME RECURRENCE: Do not reuse names from the 'Known entities' list. Every new NPC must have a unique name within this world instance.
Output strict JSON following this schema — all arrays are optional (omit if nothing changes):
{
  "clockUpdates": { "room:guard_alarm"?: number, "world:famine"?: number },
  "npcUpdates": [{
    "id": string,
    "moveTo": "x_y",
    "intentions": string[],
    "relationship": number,
    "memoryAdd": string,
    "activeEffects": string[],
    "dormantEffectsAdd": { "label": string, "source": string, "attr": "STR"|"DEX"|"CON"|"INT"|"WIS"|"CHA"|"PER"|"LCK"|"WIL"|"FOR"|"AGI"|"PRS" }
  }],
  "questUpdates": [{ "id": string, "newStatus": "active"|"failed"|"dormant", "note": string }],
  "newNodes": [{ "key": "x_y_z", "name": string, "desc": string }],
  "newNpcs": [{ "location": "x_y_z", "name": string, "identity": string, "lore": string, "sex": "male"|"female"|"non-binary", "intentions": string[] }],
  "newItems": [{ "location": "x_y_z", "name": string }],
  "worldEvents": [string],
  "roomFlagUpdates": [{ "roomKey": "x_y_z", "flags": { [key]: value } }]
}
Rules:
- Keep changes MINIMAL. Not every tick changes everything. Prefer returning empty arrays over inventing changes.
- newNpcs: Spontaneously populate the world. Use pre-forged archetypes or logical locals (creatures, traders, travelers). Max 1 per tick. Key "location" must match an existing atlas node.
- newItems: Spontaneously spawn logical objects in rooms (discarded notes, forage, loot). Max 2 per tick.
- clockUpdates: advance or relieve only clocks already present in prompt context. Prefer scoped keys exactly as shown, such as "room:guard_alarm" or "world:famine".
- NPCs move to adjacent atlas nodes (valid x+1/y+1/z+1 offset from current). Max 2 NPC moves per tick.
- Quest status transitions are conservative in world ticks: potential→active (new lead appears), active→failed (deadline/irreversible loss), dormant→active (reawakened lead). Never regress.
- Do NOT mark quests completed in world ticks. Player-driven actions are required for completion.
- New atlas nodes: max 2 per tick. key must be adjacent to an existing node (offset ±1 on one axis only).
- worldEvents: 1–3 brief strings describing what happened in the world (past tense, ≤25 words each). Reference specific NPCs or locations for grounding.
- NPCs in combat rooms (player's current location during active combat) must NOT move.
- relationship: update only if NPC's opinion of player materially changes. Range -100 to 100.
- memoryAdd: a compact string (≤20 words) capturing a significant event from this tick.
- activeEffects: current status labels for that NPC (0-4 concise ALL_CAPS labels, e.g. "WOUNDED", "ALERT", "BLESSED"). Omit if unchanged.
- activeEffects forging contract: prefer concise semantic labels with one leading emoji when clear (e.g. "🔥 WOUNDED", "🌟 BLESSED"), so UI chips can render AI-defined intent directly.
- dormantEffectsAdd: archive one expired status effect when it naturally ends.
- Do NOT invent new NPC ids; only update ids present in the prompt.
- Output ONLY valid JSON. No prose outside JSON.`
export function buildWorldTickPrompt(state: GameState, triggeredSpawns: string[] = []): string {
  const clock = state.world.clock
  const clockStr = `Day ${clock.day}, ${formatClock(clock)}`
  const playerLoc = locationKey(state.player.location)
  const lastTick = state.world.lastTick ?? 0
  const elapsed = totalMinFromClock(clock) - lastTick
  const npcList = (state.world.npcs ?? []).map(n =>
    `id=${n.id} name="${n.name}" (${n.identity}) at=${n.location} rel=${n.relationship ?? 0} fx=${(n.activeEffects ?? []).slice(0, 3).join('|') || 'none'} mem=${(n.memory ?? []).slice(-2).join('; ')} intentions=${JSON.stringify(n.intentions)}`
  ).join('\n') || 'none'
  const knownEntities = [
    state.player.name || state.player.identity || 'Player',
    ...(state.companions ?? []).map(c => c.name),
    ...(state.world.npcs ?? []).map(n => n.name),
  ]
    .map(value => String(value ?? '').trim())
    .filter(Boolean)
  const knownEntityList = [...new Set(knownEntities)].slice(0, 40).join(', ') || 'none'
  const questList = state.journal
    .filter(q => q.status === 'active' || q.status === 'potential')
    .map(q => `id=${q.id} "${q.title}" [${q.status}]`)
    .join('\n') || 'none'
  const roomClockList = (state.currentRoom.clocks ?? []).map(clock => `room:${clock.id} ${formatProgressClock(clock)}`).join('\n') || 'none'
  const worldClockList = (state.world.clocks ?? []).map(clock => `world:${clock.id} ${formatProgressClock(clock)}`).join('\n') || 'none'
  const actionEnvelopeCtx = buildActionEnvelopeContext({
    source: 'system',
    mode: 'do',
    input: `Advance the world by ${elapsed} game-minutes`,
    actor: { kind: 'system', id: 'world_sim', label: 'World Simulation' },
    targets: [
      { kind: 'world', id: 'world', label: 'World' },
      { kind: 'room', id: playerLoc, label: state.currentRoom.name || playerLoc },
    ],
    args: { elapsedMinutes: elapsed },
    tags: ['world_tick'],
  })
  
  const spawnInstruction = triggeredSpawns.length > 0 
    ? `\nSPAWN TRIGGERS: The following archetypes have appeared in the current location. You MUST instantiate them in the newNpcs array using their defined friendliness and alertness: [${triggeredSpawns.join(', ')}]`
    : ''

  // Compact atlas: existing nodes
  const nodeKeys = Object.keys(state.atlas.nodes).slice(0, 20).join(', ')
  return `World: "${state.world.seed.slice(0, 100)}"
Clock: ${clockStr} (${elapsed} minutes have passed since last tick)
Player: ${state.player.identity} at ${playerLoc}${state.combat ? ' [IN COMBAT — NPCs at player location must NOT move]' : ''}
Known entities: ${knownEntityList}${actionEnvelopeCtx}
Atlas nodes: ${nodeKeys}
Room clocks:
${roomClockList}
World clocks:
${worldClockList}
Active NPCs:
${npcList}
Active quests:
${questList}
World narrative: ${(state.world.narrative ?? '').slice(0, 200) || 'not yet established'}${spawnInstruction}

Advance the world by ${elapsed} game-minutes. Move NPCs, progress quests, add world events.`
}

export const WORLD_ENRICH_SYSTEM = `You are the World Narrative Weaver. Enrich the living world with deeper lore.
Output strict JSON:
{
  "narrative": string,
  "newQuests": [{ "id": string, "title": string, "desc": string, "type": "quest"|"mini-quest"|"want"|"wish", "status": "potential"|"dormant" }],
  "npcEnrichments": [{ "id": string, "intentions": string[], "lore": string, "relationship": number, "memoryAdd": string }]
}
Rules:
- narrative: 2–4 sentences of overarching world lore. Evocative, present-tense, ties to world seed.
- newQuests: 0–2 new quests that emerge from the world state. status must be "potential" or "dormant".
- npcEnrichments: optional deeper backstory/intentions/relationship/memory for existing NPCs.
- Output ONLY valid JSON.`
export function buildWorldEnrichPrompt(state: GameState): string {
  const npcList = (state.world.npcs ?? []).slice(0, 8).map(n =>
    `id=${n.id} "${n.name}" (${n.identity}) rel=${n.relationship ?? 0} mem=${(n.memory ?? []).slice(-2).join('; ')}: ${n.lore.slice(0, 60)}`
  ).join('\n') || 'none'
  const questList = state.journal.slice(0, 5).map(q =>
    `"${q.title}" [${q.status}]`
  ).join(', ') || 'none'
  return `World seed: "${state.world.seed}"
Player: ${state.player.identity} — ${state.player.bio.slice(0, 100)}
Existing narrative: ${(state.world.narrative ?? '').slice(0, 200) || 'none yet'}
World NPCs:
${npcList}
Known quests: ${questList}

Generate enriched world narrative and optional new plot hooks.`
}

export const PREFORGE_DRAFT_SYSTEM = `You are the Preforge Architect. Expand an admin's plain-English brief into a structured, reviewable preforge draft.
Output strict JSON:
{
  "brief": string,
  "intentSummary": string,
  "analysis": {
    "scopeSummary": string,
    "keyEntities": string[],
    "assumptions": string[],
    "ambiguities": string[],
    "generationNotes": string[]
  },
  "sourceWorld": { "kind": "liveRun"|"savedWorld"|"templateWorld"|"blankPreset", "saveId"?: string, "templateId"?: string, "presetId"?: string },
  "recipe": {
    "scale": "small"|"medium"|"large",
    "density": { "atlasNodes": number, "rooms": number, "npcs": number, "companions": number, "items": number, "quests": number, "clocks": number, "events": number },
    "tone": { "mood": string, "dangerLevel": "low"|"medium"|"high", "socialFocus": number, "explorationFocus": number, "combatFocus": number },
    "constraints": { "tokenBudget": number, "timeBudgetMs": number, "forbiddenTopics": string[], "keepOutRegions": string[] },
    "outputMode": "world-only"|"full-reality"|"starter-only"|"published-offer"
  },
  "seed": { "mode": "create"|"select", "code": string, "title": string, "summary": string },
  "vessel": { "mode": "create"|"select", "code": string, "name": string, "identity": string, "lineage": string, "sex": "female"|"male"|"unspecified", "bio": string },
  "textlets": { "worldLore": string, "openingPitch": string, "npcThreads": string, "questThreads": string, "itemHooks": string, "roomSeeds": string },
  "notes": string[]
}
Rules:
- The brief is the source of truth and should be preserved in the brief field.
- Extract a concise intentSummary that says what the world is trying to be.
- Add an analysis block with the world scope, key entities, assumptions, ambiguities, and generation notes.
- Translate the brief into concrete numbers, counts, and named textlets the admin can edit.
- Prefer dense tester-ready output over thin shells.
- Seed and vessel must each have obvious create/select intent.
- Output ONLY valid JSON. No prose.`

export function buildPreforgeDraftPrompt(
  brief: string,
  recipe: PreforgeRecipe,
  draft: Partial<PreforgeDraft> = {},
  sourceWorld: PreforgeSourceWorldRef | null = recipe.sourceWorld,
): string {
  const source = sourceWorld ?? recipe.sourceWorld
  const seed = draft.seed ?? { mode: 'create', code: '', title: '', summary: '' }
  const vessel = draft.vessel ?? { mode: 'create', code: '', name: '', identity: '', lineage: '', sex: 'unspecified', bio: '' }
  const textlets = draft.textlets ?? {
    worldLore: '',
    openingPitch: '',
    npcThreads: '',
    questThreads: '',
    itemHooks: '',
    roomSeeds: '',
  }
  return [
    `Brief: "${String(brief ?? '').trim()}"`,
    `Source world: ${JSON.stringify(source)}`,
    `Recipe seed: ${JSON.stringify(recipe)}`,
    `AI selection: ${JSON.stringify(recipe.aiSelection)}`,
    `Current seed draft: ${JSON.stringify(seed)}`,
    `Current vessel draft: ${JSON.stringify(vessel)}`,
    `Current textlets: ${JSON.stringify(textlets)}`,
    '',
    'Expand the brief into a structured review draft for the admin.',
    'Also extract an intent summary plus analysis notes for scope, key entities, assumptions, ambiguities, and generation guidance.',
  ].join('\n')
}

// ---- World Forge: batch-enrich scaffold nodes in a single AI call ----

export const WORLD_FORGE_SYSTEM = `You are the World Forge. Given a world seed and a list of placeholder atlas nodes, generate rich content for each node in a single response.
Output strict JSON:
{
  "nodes": [
    {
      "key": "x_y_z",
      "name": string,
      "desc": string,
      "items": string[],
      "npcs": [
        { "name": string, "identity": string, "lore": string, "sex": "male"|"female",
          "intentions": string[], "relationship": 0, "memory": [], "inventory": string[] }
      ],
      "spawns": [
        { "archetype": string, "level": number, "chancePerHour": number, "lootInfo": string, "friendliness": number, "alertness": string }
      ]
    }
  ]
}
Rules:
- Generate content for ALL node keys provided. Do not skip any.
- Each node name should be evocative and setting-appropriate (2-5 words).
- Each desc is 1-2 sentences describing the location's atmosphere and notable features.
- items: 0-3 discoverable items per node, fitting the world seed. Not every node needs items.
- npcs: 0-2 NPCs per node. Most nodes should have 0-1 NPCs. Not every node needs NPCs.
- spawns: 0-3 rules for spontaneous generation of creatures, traders, or dynamic events per node.
  - chancePerHour: probability (0.0 to 1.0) of spawning in one game hour.
  - friendliness: -10 (hostile/kill-on-sight) to +10 (devoted).
  - level: -2 to +5 relative to player.
  - alertness: string (e.g., "sleeping", "patrolling", "ambush").
- NPC names must be unique across all nodes and consistent with the world seed.
- Nodes closer to the origin (distance 1) should feel more populated and connected to the starting area.
- Nodes farther away (distance 2) should feel more frontier/wilderness/remote.
- All content must be tonally consistent with the world seed genre and setting.
- Keep descriptions compact — this is discovery content, not full narration.
- Output ONLY valid JSON. No prose outside JSON.`
function describePreforgeSourceWorld(sourceWorld: PreforgeRecipe['sourceWorld']): string {
  switch (sourceWorld.kind) {
    case 'liveRun':
      return `live run ${sourceWorld.saveId}`
    case 'savedWorld':
      return `saved world ${sourceWorld.saveId}`
    case 'templateWorld':
      return `template world ${sourceWorld.templateId}`
    case 'blankPreset':
      return `blank preset ${sourceWorld.presetId}`
  }
}

export interface WorldForgePromptContext {
  brief?: string
  recipe?: PreforgeRecipe
  draft?: PreforgeDraft
}

export function buildWorldForgePrompt(
  worldSeed: string,
  startingRoom: string,
  nodeKeys: string[],
  context?: WorldForgePromptContext,
): string {
  const keyList = nodeKeys.map(k => {
    const parts = k.split('_').map(Number)
    const dist = Math.abs(parts[0] || 0) + Math.abs(parts[1] || 0)
    return `${k} (distance ${dist} from origin)`
  }).join('\n')

  const brief = String(context?.draft?.brief ?? context?.brief ?? '').trim()
  const recipe = context?.recipe
  const density = recipe
    ? `atlasNodes=${recipe.density.atlasNodes}, rooms=${recipe.density.rooms}, npcs=${recipe.density.npcs}, companions=${recipe.density.companions}, items=${recipe.density.items}, quests=${recipe.density.quests}, clocks=${recipe.density.clocks}, events=${recipe.density.events}`
    : ''
  const tone = recipe
    ? `${recipe.tone.mood}; danger=${recipe.tone.dangerLevel}; social=${recipe.tone.socialFocus}; exploration=${recipe.tone.explorationFocus}; combat=${recipe.tone.combatFocus}`
    : ''
  const sourceWorld = recipe ? describePreforgeSourceWorld(recipe.sourceWorld) : ''
  const seedSummary = String(context?.draft?.seed?.summary ?? '').trim()
  const vesselSummary = [context?.draft?.vessel?.name, context?.draft?.vessel?.identity].filter(Boolean).join(' · ')

  return `World seed: "${worldSeed.slice(0, 200)}"
Starting location: "${startingRoom}"
${brief ? `Brief: "${brief}"\n` : ''}${seedSummary ? `Seed artifact: "${seedSummary}"\n` : ''}${vesselSummary ? `Vessel artifact: "${vesselSummary}"\n` : ''}${sourceWorld ? `Source world: ${sourceWorld}\n` : ''}${density ? `Density target: ${density}\n` : ''}${tone ? `Tone: ${tone}\n` : ''}

Generate content for these atlas nodes:
${keyList}

Ensure geographic and narrative coherence: nearby nodes should relate to each other and to the starting location.`
}

// Build a combat context summary string for the narrator
export function buildCombatSummary(combat: CombatState | null | undefined): string | undefined {
  if (!combat) return undefined
  const recent = combat.log.slice(-12)
  if (!recent.length) return undefined
  return recent.map(e => {
    const who = e.actor === 'player'
      ? 'You'
      : e.actor === 'companion'
        ? (e.actorName || 'Companion')
        : (e.actorName || combat.npc.name)
    const action = String(e.action ?? '').trim()
    return `[R${e.round}] ${who}: ${action || 'acts'} => ${e.outcome}`
  }).join('; ')
}
